<template>
  <Card additional-classes="px-3" v-if="booking">
    <div class="p-3 d-flex flex-column h-100" :class="containerClasses">
      <div>
        <p class="heading text-primary-black">{{ t('travellers.title') }}</p>
        <TravellersList
          :travellers="booking.travellers"
          :departure-date="booking.departureDate"
        />
      </div>
      <!-- Show button for non-UK users, message for UK users -->
      <EditTravellersButton v-if="editComponent === 'button' && !isReferenceEqualToBO" />
      <TravellerModifyMessage v-else-if="editComponent === 'message'" />
    </div>
    <TravellersPanel />
  </Card>
</template>

<script lang="ts" setup>
  // Vue
  import { ComputedRef, computed, defineProps } from 'vue'

  // Components
  import Card from '@/components/UI/Card.vue'
  import TravellersList from './TravellersList.vue'
  import EditTravellersButton from './EditTravellersButton.vue'
  import TravellerModifyMessage from './TravellerModifyMessage.vue'
  import TravellersPanel from './TravellersPanel.vue'

  // Config
  import { ModalId } from '@/config/modalIds'

  // Types
  import { BookingSummary } from '@/services/BookingService.interface'

  // Composables
  import { useBookingStatus } from '@/composables/useBookingStatus'
  import { isBookingNotYetInIresa } from '@/utils/bookingIresa'

  // CSE
  import CSEService from '@/services/CSEService'

  // Store
  import { useStore } from '@/store'
  const store = useStore()

  // Translations
  import { useI18n } from 'vue-i18n'
  const { t } = useI18n()

  // Props
  interface Props {
    booking: BookingSummary | null
  }
  const props = defineProps<Props>()

  // Computed
  const { isReservationStatus } = useBookingStatus(props.booking)

  // UK locale check
  const isUK = computed<boolean>(() => {
    return store.getters.getLocale === 'en-GB'
  })

  const isReferenceEqualToBO = computed<boolean>(() =>
    isBookingNotYetInIresa(props.booking)
  )

  // Determine what to show based on conditions
  const shouldShowEditOptions = computed<boolean>(() => {
    const cseService = new CSEService()
    // For CSE companies, don't show anything
    if (cseService.isCgosOrCGOSPM) {
      return false
    } else {
      // For all other cases, show either button or message
      return true
    }
  })

  // Determine which component to show
  const editComponent = computed<string>(() => {
    if (!isReservationStatus.value || !shouldShowEditOptions.value) {
      return 'none'
    } else if (isUK.value) {
      return 'message'
    } else {
      return 'button'
    }
  })

  // Conditional container classes - only add justify-content-between and gap when there's a button
  const containerClasses = computed<string>(() => {
    return editComponent.value === 'button' ? 'justify-content-between gap-2' : 'gap-1'
  })
</script>
